/**
 * Read model of the job-status API under `/jobs` on the API host (BDA-6736,
 * backend ADR-0048/0049). A user action (e.g. a P21 upload) starts a chain: a
 * sequence of tasks sharing the chain id as `correlationId`. IDs are UUID
 * strings, timestamps ISO-8601 strings, durations ISO-8601 durations.
 */

export type BdJobChainStatus = 'OPEN' | 'SUCCESS' | 'FAILED';

export type BdJobTaskStatus =
  | 'WAITING'
  | 'IN_PROGRESS'
  | 'SUCCESS'
  | 'FAILED'
  | 'CANCELLED';

export type BdJobLane = 'HEAVY' | 'LIGHT';

/** A resource a chain belongs to: a clinic or the import/export row that started it. */
export interface BdJobChainSubject {
  id: string;
  type: string;
}

/**
 * Subject types the backend attaches to chains. The start endpoints return
 * the row, not the chain: a chain is found by its row's subject.
 */
export const BD_JOB_SUBJECT_TYPE = {
  virtualClinic: 'virtual-clinic',
  p21Import: 'de-virtual-clinic.p21-import',
  referralImport: 'virtual-clinic-referral-import',
  supplementaryImport: 'de-virtual-clinic.supplementary-import',
  referralExport: 'de-virtual-clinic.referral-export',
  masterExport: 'de-virtual-clinic.master-export',
  decisionCockpitExport: 'de-virtual-clinic.decision-cockpit-export',
} as const;

export interface BdJobProgress {
  /** 0..100, `null` when the handler reports no value. */
  percent: number | null;
  /** `""` when there is no message. */
  message: string;
}

export interface BdJobChainSteps {
  /** Grows while the chain runs (follow-up tasks). */
  total: number;
  done: number;
  running: number;
  waiting: number;
  /** FAILED + CANCELLED. */
  failed: number;
}

export interface BdJobChainCurrentTask {
  /** Task type of the running member; with several, the one started first. */
  type: string;
  progress: BdJobProgress | null;
}

export interface BdJobChainSummaryResponse {
  /** Chain id = `correlationId` of its tasks. */
  id: string;
  kind: string;
  status: BdJobChainStatus;
  payload: Record<string, unknown> | null;
  /** Sorted by type, id. */
  subjects: BdJobChainSubject[];
  /** User id; `null` when the system started the chain. */
  createdBy: string | null;
  createdAt: string;
  closedAt: string | null;
  steps: BdJobChainSteps;
  current: BdJobChainCurrentTask | null;
}

export interface BdJobChainResponse extends BdJobChainSummaryResponse {
  /** All members, oldest first. */
  tasks: BdJobTaskStatusResponse[];
}

export type BdJobTaskErrorReason =
  | 'handler-failed'
  | 'result-not-serializable'
  | 'no-handler'
  | 'vm-error'
  | 'lease-expired'
  | 'superseded'
  | 'cancelled';

/**
 * Users who do not own the chain get only `reason`, `attempt` and
 * `supersededBy`; the rest is for the owner and `/jobs/admin/*`.
 */
export interface BdJobTaskError {
  reason: BdJobTaskErrorReason;
  attempt?: number;
  /** Task id; only for `reason = superseded`. */
  supersededBy?: string;
  message?: string;
  /** Exception class name. */
  exception?: string;
  /** Only for `vm-error`. */
  error?: string;
  /** Only for `reason = cancelled`. */
  cancelledBy?: string | null;
}

export interface BdJobTaskStatusResponse {
  id: string;
  type: string;
  status: BdJobTaskStatus;
  progress: BdJobProgress | null;
  /** Only once SUCCESS. */
  result: Record<string, unknown> | null;
  error: BdJobTaskError | null;
  attempt: number;
  maxAttempts: number;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  /** Chain id. */
  correlationId: string | null;
}

export interface BdJobQueueStatusResponse {
  /** E.g. `de-virtual-clinic`. */
  queue: string;
  lane: BdJobLane;
  waiting: number;
  inProgress: number;
  /** ISO-8601 duration of the oldest due WAITING task. */
  oldestWaitingAge: string | null;
  failedLast24h: number;
}

export interface BdJobSubscriptionStatusResponse {
  /** Name of the event cursor. */
  subscription: string;
  windowLastId: number;
  hasOpenWindow: boolean;
  updatedAt: string;
  /** Events behind the newest; a lag that keeps growing means a stuck dispatcher. */
  lag: number;
}
